import type { PuzzleSummary } from "./types";

/**
 * Which puzzles end up on a printed sheet.
 *
 * The choice is derived from a seed rather than drawn at random, so the sheet in
 * someone's hand has an address: the same URL prints the same puzzles next week, on
 * another machine, after a deploy. "Print me another set" is a new seed, not a
 * different code path.
 */

/**
 * A4 is 210×297mm and Letter is 216×279mm; the sheet is laid out for the 182×251mm
 * safe area they share. FURNITURE is what the sheet spends on its own head, footnote
 * and row gaps, so the rest is what the boards may occupy — asserted in the tests, not
 * eyeballed in a preview.
 */
export const SHEET_SAFE_MM = { width: 182, height: 251 } as const;
export const SHEET_FURNITURE_MM = 52;
export const BOARD_CAPTION_MM = 8;
export const BOARD_GAP_MM = { row: 6, column: 10 } as const;

export function printBoardsBudgetMm(): number {
  return SHEET_SAFE_MM.height - SHEET_FURNITURE_MM;
}

/** How many boards fit that safe area at each grid size. A 10×10 at a size worth
    writing in takes most of a page on its own, so it gets one. */
const PER_PAGE: Record<number, number> = { 5: 4, 10: 1, 15: 1 };

/**
 * How many puzzles the builder will draw at once. The ceiling is set by the answer
 * key: sixteen is the largest number that still lands on a single sheet at a size the
 * pictures are readable, which the tests check rather than trust.
 */
export const MAX_PRINT_COUNT = 16;

/** The printed answer grids are all drawn to this width whatever the grid size, so the
    key reads as a contact sheet rather than three sizes of thumbnail. The column and
    gap are what the stylesheet lays them out on; they live here so the "ten answers
    still fit one sheet" promise can be asserted rather than eyeballed. */
export const ANSWER_SPAN_MM = 32;
export const ANSWER_COLUMN_MM = 34;
export const ANSWER_GAP_MM = { row: 6, column: 6 } as const;
/** Caption plus the gap under it, per answer. */
export const ANSWER_CAPTION_MM = 9;

/** How many answer grids fit across the safe width. */
export function answerColumns(): number {
  return Math.floor((SHEET_SAFE_MM.width + ANSWER_GAP_MM.column) / (ANSWER_COLUMN_MM + ANSWER_GAP_MM.column));
}

/** Boards per row on a sheet: only the 5×5 fits two across. */
export function printColumns(size: number): number {
  return printPerPage(size) >= 4 ? 2 : 1;
}

export const PRINT_SIZES = [5, 10, 15] as const;
export type PrintSize = (typeof PRINT_SIZES)[number];

export function isPrintSize(value: unknown): value is PrintSize {
  return PRINT_SIZES.includes(value as PrintSize);
}

export function printPerPage(size: number): number {
  return PER_PAGE[size] ?? 1;
}

export function printCountOptions(): number[] {
  return Array.from({ length: MAX_PRINT_COUNT }, (_, index) => index + 1);
}

/** Cell edge for an answer grid: every size drawn to the same overall width. */
export function answerCellMm(size: number): number {
  return ANSWER_SPAN_MM / size;
}

export function printPageCount(size: number, count: number): number {
  return Math.ceil(count / printPerPage(size));
}

/** Letters and digits are in the catalogue so the alphabet is playable, but a sheet
    headed "hidden picture" that reveals a Q is a let-down on paper too. */
export function isPrintable(summary: PuzzleSummary): boolean {
  return summary.theme !== "glyphs";
}

function hashSeed(seed: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/** mulberry32: small, seeded, and identical everywhere — which is the whole point. */
function random(state: number): () => number {
  let value = state;
  return () => {
    value = (value + 0x6d2b79f5) >>> 0;
    let result = Math.imul(value ^ (value >>> 15), 1 | value);
    result = (result + Math.imul(result ^ (result >>> 7), 61 | result)) ^ result;
    return ((result ^ (result >>> 14)) >>> 0) / 4_294_967_296;
  };
}

export function newPrintSeed(): string {
  return Math.floor(Math.random() * 0xffffff).toString(36);
}

/**
 * `count` ids of the requested size, drawn without repeats. The pool is sorted first
 * so the catalogue's own order — which changes whenever puzzles are regenerated —
 * cannot change what a seed prints.
 */
export function selectPrintable(summaries: readonly PuzzleSummary[], size: number, count: number, seed: string): string[] {
  const pool = summaries
    .filter((summary) => summary.size === size && isPrintable(summary))
    .map((summary) => summary.id)
    .sort();
  if (pool.length === 0) return [];

  const next = random(hashSeed(`${seed}:${size}`));
  // Fisher-Yates, stopped once enough have been drawn.
  const wanted = Math.min(count, pool.length);
  for (let index = 0; index < wanted; index += 1) {
    const swap = index + Math.floor(next() * (pool.length - index));
    const held = pool[index] as string;
    pool[index] = pool[swap] as string;
    pool[swap] = held;
  }
  return pool.slice(0, wanted);
}
